import React, { useEffect, useRef } from 'react';
import { ArrowRight, MapPin, Facebook } from 'lucide-react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';

const CountUp = ({ to, suffix = "" }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest) + suffix);
  const ref = useRef(null);

  useEffect(() => {
    const controls = animate(count, to, {
      duration: 2,
      ease: "easeOut",
    });
    return controls.stop;
  }, [count, to]);

  return <motion.p ref={ref} className="text-3xl font-bold">{rounded}</motion.p>;
};

const Hero = () => {
  return (
    <section className="relative h-[60vh] md:h-[90vh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://assets.auxodev.dk/assets/8283c528-e59c-4f9d-b50b-8d919a0a0240/e9b43e93-f125-4d83-977e-84cff6465f8c.jpg"
          alt="Materialeplads"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight text-white leading-tight mb-6">
              Grus, jord, flis og materialer til <span className="text-white/80">have & anlæg</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-100 mb-6 md:mb-10 leading-relaxed max-w-2xl">
              Kvalitetsmaterialer til afhentning i Grenå og Glesborg. Professionel rådgivning og hurtig service.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#priser"
              className="flex items-center justify-center gap-2 bg-black text-white px-8 py-4 rounded-none text-lg font-bold hover:bg-zinc-900 transition-all shadow-xl hover:shadow-black/20 group"
            >
              Se priser
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#lokationer"
              className="flex items-center justify-center gap-2 bg-white text-black px-8 py-4 rounded-none text-lg font-bold hover:bg-zinc-50 transition-all shadow-xl"
            >
              <MapPin size={20} />
              Find nærmeste plads
            </a>
          </motion.div>
        </div>
      </div>

      {/* Stats / Trust Badges */}
      <div className="absolute bottom-0 left-0 w-full bg-[#a3844e]/90 backdrop-blur-md py-6 border-t border-white/10 hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div className="text-white border-r border-white/10">
              <p className="text-3xl font-bold">2</p>
              <p className="text-sm text-brand-light/80 uppercase tracking-widest">Lokationer på Djursland</p>
            </div>
            <div className="text-white border-r border-white/10">
              <CountUp to={100} suffix="%" />
              <p className="text-sm text-brand-light/80 uppercase tracking-widest">Kvalitetsmaterialer</p>
            </div>
            <div className="text-white">
              <p className="text-3xl font-bold">Hurtig</p>
              <p className="text-sm text-brand-light/80 uppercase tracking-widest">Afhentning & Service</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
